import React, { useState, useEffect } from 'react';
import { ArrowLeft, Eye, EyeOff, Loader2, Zap, Mail, CheckCircle, Shield } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '@/i18n';
import { apiClient } from '@/utils/apiClient';
import { clsx } from 'clsx';

interface LoginPageProps {
  onBack: () => void;
  onSuccess: () => void;
  onForgotPassword?: () => void;
  /** Pre-fill the email field (e.g. after email verification). */
  initialEmail?: string;
}

export const LoginPage: React.FC<LoginPageProps> = ({
  onBack,
  onSuccess,
  onForgotPassword,
  initialEmail,
}) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Post-registration state
  const [registrationDone, setRegistrationDone] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);

  // Unverified email inline resend state
  const [showUnverifiedResend, setShowUnverifiedResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // SSO state
  const [ssoEnabled, setSsoEnabled] = useState(false);

  // STORY-122: hide the register form when self-registration is disabled
  const [selfRegistrationEnabled, setSelfRegistrationEnabled] = useState(true);

  const { login, register, resendVerification, pendingVerificationEmail } = useAuthStore();
  const { t } = useTranslation();

  // Check if SSO is enabled on mount
  useEffect(() => {
    apiClient.getSsoConfig()
      .then(data => setSsoEnabled(data.enabled === true))
      .catch((err) => console.warn('[SSO] config check failed:', err));
    apiClient.getRegistrationStatus()
      .then(data => setSelfRegistrationEnabled(data.self_registration_enabled !== false))
      .catch(() => { /* endpoint unavailable — keep register visible */ });
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // If initialEmail changes (e.g. after verification), fill it in
  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowUnverifiedResend(false);
    setResendMessage(null);
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        onSuccess();
      } else {
        const result = await register(username, email, password);
        setRegistrationDone(true);
        setVerificationRequired(result.emailVerificationRequired);
      }
    } catch (err: any) {
      const msg = err?.message || '';

      if (msg === 'email_not_verified') {
        // Show inline resend UI instead of a generic error
        setError(t.auth.emailNotVerifiedError);
        setShowUnverifiedResend(true);
      } else {
        setError(msg || (mode === 'login' ? t.auth.loginFailed : t.auth.registerFailed));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const emailToResend = pendingVerificationEmail || email;
    if (!emailToResend) return;

    setResendLoading(true);
    setResendMessage(null);
    try {
      const msg = await resendVerification(emailToResend);
      setResendMessage(msg);
      setResendCooldown(60); // 60-second cooldown
    } catch (err: any) {
      setResendMessage(err?.message || t.auth.resendFailed);
    } finally {
      setResendLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError(null);
    setPassword('');
    setShowPassword(false);
    setUsername('');
    setEmail(initialEmail || '');
    setShowUnverifiedResend(false);
    setRegistrationDone(false);
    setVerificationRequired(false);
    setResendMessage(null);
  };

  const goToLogin = () => {
    setMode('login');
    setShowPassword(false);
    setRegistrationDone(false);
    setVerificationRequired(false);
    setShowUnverifiedResend(false);
    setResendMessage(null);
    setError(null);
  };

  // ─── Post-registration verification notice ───
  if (registrationDone) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 transition-colors">
        <div
          className={clsx(
            'w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700',
            'bg-white dark:bg-gray-800 shadow-lg p-8 text-center'
          )}
        >
          {verificationRequired ? (
            <>
              <div className="flex justify-center mb-4">
                <Mail className="h-12 w-12 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {t.auth.checkYourEmail}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                {t.auth.verificationSent}
              </p>
              <p className="font-medium text-gray-800 dark:text-gray-200 mb-4">{email}</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
                {t.auth.verificationHint}
              </p>

              {resendMessage && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-300">
                  {resendMessage}
                </div>
              )}

              <button
                onClick={handleResendVerification}
                disabled={resendLoading || resendCooldown > 0}
                className={clsx(
                  'w-full flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm mb-3',
                  'border border-gray-300 dark:border-gray-600',
                  'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-colors'
                )}
              >
                {resendLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t.common.loading}
                  </>
                ) : resendCooldown > 0 ? (
                  `${t.auth.resendVerification} (${resendCooldown}s)`
                ) : (
                  t.auth.resendVerification
                )}
              </button>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {t.auth.accountCreated}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t.auth.accountCreatedHint}
              </p>
            </>
          )}

          <button
            onClick={goToLogin}
            className={clsx(
              'w-full flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm text-white',
              'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
              'transition-colors'
            )}
          >
            {t.auth.goToLogin}
          </button>
        </div>
      </div>
    );
  }

  // ─── Main login / register form ───
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 transition-colors">
      <div
        className={clsx(
          'w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700',
          'bg-white dark:bg-gray-800 shadow-lg p-8'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={onBack}
            className="p-2 -ml-2 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-2">
            <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <span className="font-semibold text-gray-900 dark:text-white">
              Ключ-Астром MCP
            </span>
          </div>
          <div className="w-9" />
        </div>

        <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-6">
          {mode === 'login' ? t.auth.loginTitle : t.auth.registerTitle}
        </h2>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            {error}
            {/* Inline resend for unverified email */}
            {showUnverifiedResend && (
              <div className="mt-2">
                {resendMessage && (
                  <p className="text-green-700 dark:text-green-300 mb-1">{resendMessage}</p>
                )}
                <button
                  onClick={handleResendVerification}
                  disabled={resendLoading || resendCooldown > 0}
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium disabled:opacity-50"
                >
                  {resendLoading
                    ? t.common.loading
                    : resendCooldown > 0
                    ? `${t.auth.resendVerification} (${resendCooldown}s)`
                    : t.auth.resendVerification}
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username (register only) */}
          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.auth.username}
              </label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className={clsx(
                  'w-full px-3 py-2 rounded-lg border text-sm',
                  'border-gray-300 dark:border-gray-600',
                  'bg-white dark:bg-gray-700',
                  'text-gray-900 dark:text-white',
                  'focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  'transition-colors'
                )}
                placeholder={t.auth.usernamePlaceholder}
              />
            </div>
          )}

          {/* Email (always shown) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t.auth.email}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className={clsx(
                'w-full px-3 py-2 rounded-lg border text-sm',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                'text-gray-900 dark:text-white',
                'focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                'transition-colors'
              )}
              placeholder={t.auth.emailPlaceholder}
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t.auth.password}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className={clsx(
                  'w-full px-3 py-2 pr-10 rounded-lg border text-sm',
                  'border-gray-300 dark:border-gray-600',
                  'bg-white dark:bg-gray-700',
                  'text-gray-900 dark:text-white',
                  'focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  'transition-colors'
                )}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
                title={showPassword ? t.auth.hidePassword : t.auth.showPassword}
                aria-pressed={showPassword}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Forgot password link (login only) */}
          {mode === 'login' && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                {t.auth.forgotPassword}
              </button>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={clsx(
              'w-full flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm text-white',
              'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors'
            )}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t.common.loading}
              </>
            ) : mode === 'login' ? (
              t.auth.loginButton
            ) : (
              t.auth.registerButton
            )}
          </button>
        </form>

        {/* SSO Login */}
        {ssoEnabled && mode === 'login' && (
          <div className="mt-4">
            <div className="relative flex items-center my-4">
              <div className="flex-grow border-t border-gray-300 dark:border-gray-600" />
              <span className="flex-shrink mx-3 text-xs text-gray-500 dark:text-gray-400 uppercase">
                {t.common?.or || 'or'}
              </span>
              <div className="flex-grow border-t border-gray-300 dark:border-gray-600" />
            </div>
            <a
              href={`${apiClient.apiBaseUrl}/api/auth/sso/login`}
              className={clsx(
                'w-full flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm',
                'border border-gray-300 dark:border-gray-600',
                'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700',
                'hover:bg-gray-50 dark:hover:bg-gray-600',
                'transition-colors'
              )}
            >
              <Shield className="mr-2 h-4 w-4" />
              {t.auth?.ssoLogin || 'Sign in with SSO'}
            </a>
          </div>
        )}

        {/* Toggle mode (hidden when self-registration is disabled — STORY-122) */}
        {(selfRegistrationEnabled || mode === 'register') && (
          <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            {mode === 'login' ? t.auth.noAccount : t.auth.hasAccount}{' '}
            <button
              onClick={toggleMode}
              className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              {mode === 'login' ? t.auth.registerLink : t.auth.loginLink}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
